import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "@/components/Layout";
import FlavorRadar from "@/components/FlavorRadar";
import DataState from "@/components/DataState";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Plus, Flask, Trash, PencilSimple, Sparkle, Compass, MagnifyingGlass, Microscope,
} from "@phosphor-icons/react";
import { toast } from "sonner";

import { PRODUCT_CATEGORIES, getProductType, filterByCategory } from "@/lib/product-types";

export default function Dashboard() {
  const [recipes, setRecipes] = useState([]);
  const [starters, setStarters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [activeTab, setActiveTab] = useState("all");
  const [view, setView] = useState("formulas");
  const [q, setQ] = useState("");
  const navigate = useNavigate();
  const { user } = useAuth();
  const isDemo = user?.role === "demo" || user?.is_demo;

  const load = () => {
    setLoading(true);
    setLoadError(null);
    Promise.all([api.get("/recipes"), api.get("/starters")])
      .then(([r, s]) => { setRecipes(r.data); setStarters(s.data); })
      .catch((err) => { setLoadError(err); })
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    if (!window.confirm("Delete this formulation?")) return;
    await api.delete(`/recipes/${id}`);
    toast.success("Formulation deleted");
    load();
  };

  const cloneStarter = async (key, name) => {
    try {
      const { data } = await api.post(`/starters/${key}/clone`);
      toast.success(`"${name}" added to your formulations`);
      navigate(`/builder/${data.id}`);
    } catch (err) {
      console.error(err);
      toast.error("Could not clone starter — try again");
    }
  };

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return filterByCategory(recipes, activeTab)
      .filter((r) => !s || `${r.name || ""} ${r.product_name || ""} ${r.sku || ""}`.toLowerCase().includes(s))
      .sort((a, b) => new Date(b.updated_at || 0) - new Date(a.updated_at || 0));
  }, [recipes, activeTab, q]);

  const filteredStarters = useMemo(() => {
    const s = q.trim().toLowerCase();
    return filterByCategory(starters, activeTab).filter((x) => !s || `${x.name || ""}`.toLowerCase().includes(s));
  }, [starters, activeTab, q]);

  // Counts per tab (by category)
  const counts = useMemo(() => {
    const c = {};
    PRODUCT_CATEGORIES.forEach((cat) => {
      c[cat.key] = filterByCategory(recipes, cat.key).length;
    });
    return c;
  }, [recipes]);

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#5B6570]">Projects &amp; formulation</div>
            <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-[#17324D]">Formulas</h1>
          </div>
          <Button onClick={() => navigate("/builder")} className="bg-[#17324D] hover:bg-[#22476B] text-white"
            data-testid="new-recipe-button">
            <Plus size={16} weight="bold" className="mr-2" /> New formula
          </Button>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
          <div role="tablist" aria-label="Library" className="inline-flex rounded-md border border-stone-200 bg-white p-0.5 shrink-0">
            {[["formulas", `Saved formulas (${recipes.length})`], ...(isDemo ? [] : [["templates", "Templates"]])].map(([k, label]) => (
              <button key={k} role="tab" aria-selected={view === k} onClick={() => setView(k)}
                className={`px-3 py-1.5 text-sm rounded ${view === k ? "bg-[#17324D] text-white" : "text-[#5B6570] hover:text-[#17324D]"}`}
                data-testid={`view-${k}`}>{label}</button>
            ))}
          </div>
          <div className="relative flex-1">
            <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} aria-label="Search formulas"
              placeholder={view === "templates" ? "Search templates" : "Search by name, product name or SKU"}
              className="w-full rounded-md border border-stone-300 bg-white pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#17324D]/30"
              data-testid="input-dashboard-search" />
          </div>
        </div>

        <div className="flex gap-1.5 mb-6 overflow-x-auto pb-1">
          {PRODUCT_CATEGORIES.map((cat) => {
            const active = activeTab === cat.key;
            return (
              <button key={cat.key} onClick={() => setActiveTab(cat.key)} aria-pressed={active}
                className={`shrink-0 px-3 py-1 text-xs rounded-full border transition-colors ${active ? "bg-[#EAF0F6] border-[#C5D3E2] text-[#17324D] font-semibold" : "bg-white border-stone-200 text-[#5B6570] hover:text-[#17324D]"}`}
                data-testid={`tab-${cat.key}`}>
                {cat.label}{view === "formulas" && counts[cat.key] > 0 ? ` · ${counts[cat.key]}` : ""}
              </button>
            );
          })}
        </div>

        {loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 animate-pulse">{[0, 1, 2].map((i) => <div key={i} className="h-44 rounded-lg bg-stone-100" />)}</div>
        ) : loadError ? (
          <DataState error={loadError} onRetry={load} testId="dashboard-load" />
        ) : view === "formulas" ? (
          recipes.length === 0 ? (
            <EmptyState onCreate={() => navigate("/builder")} onTemplates={isDemo ? null : () => setView("templates")} />
          ) : filtered.length === 0 ? (
            <div className="rounded-lg border border-dashed border-stone-300 bg-white p-8 text-center text-sm text-[#5B6570]">
              No formulas match{q ? ` "${q}"` : ""} in this category.
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {filtered.map((r) => (
                <RecipeCard key={r.id} recipe={r} isDemo={isDemo}
                  onEdit={() => navigate(`/builder/${r.id}`)}
                  onRD={() => navigate(`/rd/${r.id}`)}
                  onHub={r.product_type === "pet_topper" ? null : () => navigate(`/product/${r.id}`)}
                  onDelete={() => del(r.id)} />
              ))}
            </div>
          )
        ) : (
          <div id="starter-library">
            <p className="text-sm text-[#5B6570] mb-4 max-w-2xl">
              Starting-point formulations with production methods and spec sheets. Using a template copies it into your saved formulas for editing.
            </p>
            {filteredStarters.length === 0 ? (
              <div className="rounded-lg border border-dashed border-stone-300 bg-white p-8 text-center text-sm text-[#5B6570]">No templates match this filter.</div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="starter-grid">
                {filteredStarters.map((s) => (
                  <StarterCard key={s.key} starter={s} onUse={() => cloneStarter(s.key, s.name)} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}

function RecipeCard({ recipe: r, onEdit, onDelete, onHub, onRD, isDemo }) {
  const type = getProductType(r.product_type);
  const Icon = type.icon;
  return (
    <Card
      onClick={onEdit}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter") onEdit(); }}
      className={`p-5 border-stone-200 hover:-translate-y-1 hover:shadow-lg transition-all ring-1 ring-inset cursor-pointer ${type.ring}`}
      data-testid={`recipe-card-${r.id}`}>
      <div className="flex items-start justify-between">
        <div className={`h-10 w-10 rounded-md ${type.bg} ${type.text} grid place-items-center`}>
          <Icon size={20} weight="duotone" />
        </div>
        <div className="flex gap-1">
          <Button variant="ghost" size="sm"
            onClick={(e) => { e.stopPropagation(); onEdit(); }}
            data-testid={`edit-recipe-${r.id}`}
            title={isDemo ? "View formulation" : "Edit"}
          >
            <PencilSimple size={14} />
          </Button>
          {!isDemo && (
            <Button variant="ghost" size="sm"
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              data-testid={`delete-recipe-${r.id}`}>
              <Trash size={14} className="text-red-600" />
            </Button>
          )}
        </div>
      </div>
      <div className="mt-3 font-semibold text-lg">{r.product_name || r.name}</div>
      <div className="flex items-center gap-2 text-xs mt-1 flex-wrap">
        <span className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${type.bg} ${type.text}`}>
          {type.label}
        </span>
        {r.sku && <span className="font-mono text-stone-700 bg-stone-100 px-1.5 py-0.5 rounded">{r.sku}</span>}
        <span className="text-stone-500">{r.version || "v1"}</span>
        {r.status && (
          <span className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded ${
            r.status === "Production" ? "bg-emerald-100 text-emerald-800"
            : r.status === "Pilot" ? "bg-amber-100 text-amber-800"
            : r.status === "Archived" ? "bg-stone-100 text-stone-500"
            : "bg-blue-100 text-blue-800"
          }`}>{r.status}</span>
        )}
      </div>
      <div className="text-sm text-stone-500 mt-1">
        {r.ingredients.length} ingredients · {r.serving_size_g}g serving
      </div>
      <FlavorArchitecture arch={r.flavor_architecture} />
      <FlavorRadar radar={r.flavor_radar} />
      <div className="text-xs text-stone-400 mt-2">Updated {new Date(r.updated_at).toLocaleDateString()}</div>
      <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Button variant="outline" size="sm"
          className="w-full text-[#17324D] border-[#C5D3E2] hover:bg-[#EAF0F6]"
          onClick={(e) => { e.stopPropagation(); onRD(); }}
          data-testid={`rd-recipe-${r.id}`}>
          <Microscope size={14} weight="duotone" className="mr-1.5" /> R&amp;D workspace
        </Button>
        {onHub && (
          <Button variant="outline" size="sm"
            className="w-full text-[#17324D] border-[#C5D3E2] hover:bg-[#EAF0F6]"
            onClick={(e) => { e.stopPropagation(); onHub(); }}
            data-testid={`hub-recipe-${r.id}`}>
            <Compass size={14} weight="duotone" className="mr-1.5" /> Product hub
          </Button>
        )}
      </div>
    </Card>
  );
}

// ── Flavor Architecture — sensory profile bars on the formulation card ──
const ARCH_ROWS = [
  { key: "front",   label: "Front",   color: "bg-amber-400" },   // up-front impact
  { key: "body",    label: "Body",    color: "bg-emerald-500" }, // mid-palate depth
  { key: "finish",  label: "Finish",  color: "bg-sky-500" },     // lingering length
  { key: "thermal", label: "Thermal", color: "bg-rose-500" },    // blooms with heat
];

function FlavorArchitecture({ arch }) {
  if (!arch) return null;
  const hasAny = ARCH_ROWS.some((row) => Number(arch[row.key]) > 0);
  if (!hasAny) return null;
  return (
    <div className="mt-3 pt-3 border-t border-stone-100" data-testid="flavor-architecture">
      <div className="text-[10px] uppercase tracking-wider font-bold text-stone-400 mb-1.5">
        Flavor Architecture
      </div>
      <div className="grid grid-cols-[52px_1fr] gap-x-2 gap-y-1 items-center">
        {ARCH_ROWS.map((row) => {
          const v = Math.max(0, Math.min(5, Number(arch[row.key]) || 0));
          return (
            <React.Fragment key={row.key}>
              <span className="text-[11px] text-stone-500">{row.label}</span>
              <span className="flex gap-1" title={`${row.label}: ${v}/5`}>
                {[1, 2, 3, 4, 5].map((i) => (
                  <span
                    key={i}
                    className={`h-1.5 flex-1 max-w-[22px] rounded-full ${i <= v ? row.color : "bg-stone-200"}`}
                  />
                ))}
              </span>
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}

function StarterCard({ starter: s, onUse }) {
  const type = getProductType(s.product_type);
  const Icon = type.icon;
  return (
    <Card className={`p-5 border-stone-200 hover:-translate-y-1 hover:shadow-lg transition-all relative overflow-hidden ${type.bg}`}
      data-testid={`starter-card-${s.key}`}>
      <div className="absolute top-3 right-3 text-[9px] uppercase tracking-wider font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded flex items-center gap-1">
        <Sparkle size={10} weight="fill" /> Starter
      </div>
      <div className={`h-10 w-10 rounded-md bg-white ${type.text} grid place-items-center`}>
        <Icon size={20} weight="duotone" />
      </div>
      <div className="mt-3 font-semibold text-lg">{s.name}</div>
      <span className={`inline-block text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded bg-white ${type.text} mt-1`}>
        {type.label}
      </span>
      {s.description && (
        <p className="text-[12px] text-stone-700 mt-2 leading-relaxed line-clamp-3">{s.description}</p>
      )}
      <div className="text-xs text-stone-500 mt-2">
        {s.ingredients.length} ingredients
        {s.shelf_life_months ? ` · ${s.shelf_life_months}-mo shelf life` : ""}
      </div>
      <Button onClick={onUse}
        className="mt-3 w-full bg-stone-900 hover:bg-stone-800 text-white"
        data-testid={`use-starter-${s.key}`}>
        Use this template
      </Button>
    </Card>
  );
}

function EmptyState({ onCreate, onTemplates }) {
  return (
    <div className="rounded-lg border border-dashed border-stone-300 bg-white p-12 text-center" data-testid="empty-formulas">
      <div className="h-14 w-14 rounded-full bg-[#EAF0F6] text-[#17324D] grid place-items-center mx-auto">
        <Flask size={26} weight="duotone" />
      </div>
      <h3 className="text-xl font-semibold mt-4 text-[#17324D]">No saved formulas yet</h3>
      <p className="text-[#5B6570] text-sm mt-1 max-w-md mx-auto">
        Build from scratch, or copy a template as a starting point. Saved formulas get revision history, testing and decisions in their R&amp;D workspace.
      </p>
      <div className="mt-5 flex justify-center gap-2 flex-wrap">
        <Button onClick={onCreate} className="bg-[#17324D] hover:bg-[#22476B] text-white" data-testid="empty-new-button">
          <Plus size={16} weight="bold" className="mr-2" /> New formula
        </Button>
        {onTemplates && <Button variant="outline" onClick={onTemplates} data-testid="empty-templates-button">Browse templates</Button>}
      </div>
    </div>
  );
}
